import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

type NavState = 'top' | 'solid' | 'hidden'

const navAnim = {
  top: {
    y: 0,
    backgroundColor: 'rgba(12,12,12,0)',
    backdropFilter: 'blur(0px)',
    borderBottom: '1px solid rgba(232,255,0,0)'
  },
  solid: {
    y: 0,
    backgroundColor: 'rgba(12,12,12,0.95)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(232,255,0,0.12)'
  },
  hidden: {
    y: -100,
    backgroundColor: 'rgba(12,12,12,0.95)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(232,255,0,0.12)'
  }
}

export function Nav() {
  const [state, setState] = useState<NavState>('top')

  useEffect(() => {
    let lastY = 0
    const handler = () => {
      const y = window.scrollY
      if (y < 80) {
        setState('top')
      } else if (y > lastY && y > 300) {
        setState('hidden')
      } else {
        setState('solid')
      }
      lastY = y
    }

    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  return (
    <motion.nav
      animate={navAnim[state]}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '0 max(5vw, 24px)',
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      {/* LOGO */}
      <motion.a
        href="/"
        whileHover={{ letterSpacing: '0.04em' }}
        transition={{ duration: 0.3 }}
        style={{
          fontFamily: 'Bebas Neue',
          fontSize: '2rem',
          color: 'var(--white)',
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1
        }}
      >
        <span>
          A<span style={{ color: 'var(--lime)' }}>SHU</span>
        </span>
        <span
          style={{
            fontFamily: 'DM Sans',
            fontSize: '0.55rem',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '0.25em',
            fontWeight: 500
          }}
        >
          CLEANING SERVICE
        </span>
      </motion.a>

      {/* LINKS — desktop */}
      <div
        style={{
          display: 'flex',
          gap: 40,
          alignItems: 'center'
        }}
        className="hidden md:flex"
      >
        {['Home', 'Services', 'About', 'Gallery', 'Contact'].map((link) => (
          <motion.a
            key={link}
            href={`/${link.toLowerCase()}`}
            data-cursor="hover"
            style={{
              color: 'var(--white-60)',
              fontFamily: 'DM Sans',
              fontWeight: 500,
              fontSize: '0.9rem',
              textDecoration: 'none',
              position: 'relative'
            }}
            whileHover={{ color: 'var(--white)' }}
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {link}
            {/* Underline */}
            <motion.span
              style={{
                position: 'absolute',
                bottom: -4,
                left: 0,
                height: '1px',
                background: 'var(--lime)',
                width: '100%',
                scaleX: 0,
                originX: 0
              }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.a>
        ))}
      </div>

      {/* CTA */}
      <motion.a
        href="tel:+251997745317"
        data-cursor="hover"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        style={{
          background: 'var(--lime)',
          color: 'var(--black)',
          fontFamily: 'DM Sans',
          fontWeight: 800,
          fontSize: '0.9rem',
          padding: '10px 24px',
          borderRadius: 'var(--radius-full)',
          textDecoration: 'none',
          display: 'flex',
          gap: 8,
          alignItems: 'center'
        }}
        animate={{
          boxShadow: [
            '0 0 0px rgba(232,255,0,0)',
            '0 0 30px rgba(232,255,0,0.4)',
            '0 0 0px rgba(232,255,0,0)'
          ]
        }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        Book Now
      </motion.a>
    </motion.nav>
  )
}
